import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import HomePage from "@/pages/HomePage";
import ProductListPage from "@/pages/ProductListPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "@/pages/AdminPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import { ShippingPolicy, RefundPolicy, PrivacyPolicy, TermsOfService } from "@/pages/PolicyPages";
import TrackOrderPage from "@/pages/TrackOrderPage";
import JewelryPage from "@/pages/JewelryPage";
import AdminOrdersPage from "@/pages/AdminOrdersPage";
import AdminLateOrdersPage from "@/pages/AdminLateOrdersPage";
import LateOrderPage from "@/pages/LateOrderPage";
import AboutPage from "@/pages/AboutPage";
import CollectionPage from "@/pages/CollectionPage";
import FaqPage from "@/pages/FaqPage";
import ContactPage from "@/pages/ContactPage";
import NotFoundPage from "@/pages/NotFoundPage";
import EmailSignup from "@/components/EmailSignup";
import { trackPageView } from "@/lib/meta";
import { captureAttribution } from "@/lib/attribution";
import { Navigate } from "react-router-dom";

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView();
    // Re-capture attribution on every navigation (picks up _ga cookie if it appeared later)
    captureAttribution();
  }, [location.pathname]);
  return null;
}

function AppContent() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageTracker />
      <Navbar />
      <CartDrawer />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop/:category" element={<ProductListPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/orders" element={<AccountPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/track-order" element={<TrackOrderPage />} />
          <Route path="/jewelry" element={<JewelryPage />} />
          <Route path="/orders/*" element={<Navigate to="/track-order" replace />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
          <Route path="/admin/late-orders" element={<AdminLateOrdersPage />} />
          <Route path="/late-order" element={<LateOrderPage />} />
          <Route path="/policies/shipping" element={<ShippingPolicy />} />
          <Route path="/policies/refunds" element={<RefundPolicy />} />
          <Route path="/policies/privacy" element={<PrivacyPolicy />} />
          <Route path="/policies/terms" element={<TermsOfService />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <EmailSignup />
      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
